import { useUserStore } from '@/stores/userStore';
import { ROLES } from '@teamworksdev/influencer/core';
import type { RouteRecordRaw } from 'vue-router';
import { accessTasks } from '@/permissions/exchange';

const routes: RouteRecordRaw[] = [
	{
		path: '/compliance',
		beforeEnter: [canAccessComplianceRoutes],
		children: [
			{
				path: 'dashboard',
				name: 'ComplianceDashboard',
				component: () => import('@/views/ComplianceDashboard.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceDashboard.metaTitle',
					requiredAccess: 'isComplianceOfficer',
				},
			},
			{
				path: 'transactions',
				name: 'ComplianceTransactionsParent',
				component: () => import('@/views/ExchangeTransactionsParent.vue'),
				beforeEnter: [canAccessTransactions],
				children: [
					{
						path: '',
						name: 'ComplianceTransactions',
						component: () => import('@/views/TransactionsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTransactions.metaTitle',
							requiredAccess: 'canAccessTransactions',
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTransactions'
									: 'ExchangeTransactions';
							},
						},
					},
					{
						path: 'agreements',
						name: 'ComplianceAgreements',
						component: () => import('@/views/AgreementsIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeAgreements.metaTitle',
							requiredAccess: 'canAccessTransactions',
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceAgreements'
									: 'ExchangeAgreements';
							},
						},
					},
					{
						path: 'tasks',
						name: 'ComplianceTasks',
						component: () => import('@/views/TasksIndex.vue'),
						meta: {
							requiresAuth: true,
							layout: 'LayoutAuthenticated',
							title: 'pageExchangeTasks.metaTitle',
							permissionRule: accessTasks,
							requiredLDFlags: ['tasks-v2'],
							profileSwitchRedirect: () => {
								const userStore = useUserStore();
								return userStore.profileAccess.isComplianceOfficer
									? 'ComplianceTransactions'
									: 'ExchangeTransactions';
							},
						},
					},
				],
			},
			{
				path: 'transactions/:transactionUuid',
				name: 'ComplianceTransaction',
				component: () => import('@/views/ExchangeTransaction.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageTransaction.metaTitle',
					requiredAccess: 'canAccessTransactions',
					profileSwitchRedirect: 'ComplianceTransactions',
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'agreements',
				redirect: { name: 'ComplianceAgreements' },
			},
			{
				path: 'agreements/:agreementUuid',
				name: 'ComplianceAgreement',
				component: () => import('@/views/ExchangeAgreement.vue'),
				props: true,
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageAgreement.metaTitle',
					requiredAccess: 'canAccessTransactions',
				},
				beforeEnter: [canAccessTransactions],
			},
			{
				path: 'settings',
				name: 'ComplianceSettingsParent',
				component: () => import('@/views/ComplianceSettingsParent.vue'),
				redirect: { name: 'ComplianceSettingsSchool' },
				children: [
					{
						path: 'school',
						name: 'ComplianceSettingsSchool',
						component: () => import('@/views/ComplianceSettingsSchool.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsSchool.metaTitle',
							requiredAccess: 'isComplianceOfficer',
						},
					},
					{
						path: 'local-exchange',
						name: 'ComplianceSettingsExchange',
						component: () => import('@/views/ComplianceSettingsExchange.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsExchange.metaTitle',
							requiredAccess: complianceUserWithLocalExchangeEnabled,
						},
					},
					{
						path: 'users',
						name: 'ComplianceSettingsUsers',
						component: () => import('@/views/ComplianceSettingsUsers.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsUsers.metaTitle',
							requiredAccess: 'isComplianceOfficer',
						},
					},
					{
						path: 'athlete-exports',
						name: 'ComplianceSettingsAthleteExports',
						component: () => import('@/views/ComplianceSettingsAthleteExports.vue'),
						meta: {
							layout: 'LayoutAuthenticated',
							requiresAuth: true,
							title: 'pageComplianceSettingsAthleteExports.metaTitle',
							requiredAccess: 'isComplianceOfficer',
						},
					},
				],
			},
			{
				path: 'brands',
				name: 'ComplianceBrands',
				component: () => import('@/views/ComplianceBrands.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceBrands.metaTitle',
					requiredAccess: complianceUserWithLocalExchangeEnabled,
				},
			},
			{
				path: 'agents',
				name: 'ComplianceAgents',
				component: () => import('@/views/ComplianceAgents.vue'),
				meta: {
					requiresAuth: true,
					layout: 'LayoutAuthenticated',
					title: 'pageComplianceAgents.metaTitle',
					requiredAccess: 'isComplianceOfficer',
				},
			},
		],
	},
];

function complianceUserWithLocalExchangeEnabled() {
	const userStore = useUserStore();
	return userStore.profile?.role_id === ROLES.COMPLIANCE_OFFICER
		&& userStore.profile.data.school.is_local_exchange_enabled;
}

function canAccessComplianceRoutes() {
	const userStore = useUserStore();
	if (!userStore.profileAccess.isComplianceOfficer) {
		return { name: 'NotFound' };
	}
}

function canAccessTransactions() {
	const userStore = useUserStore();
	if (!userStore.profileAccess.canAccessTransactions) {
		return { name: 'NotFound' };
	}
}

export default routes;
